// Dev-only guided checklist for the teacher-feedback manual smoke.
// State lives in sessionStorage so closing the dialog does not wipe the run.
// Production builds never include this module (@dev-only resolves to stubs).

import { reactive } from 'vue'

const STORAGE_KEY = 'cmv-feedback-smoke-guide'

export const FEEDBACK_SMOKE_STEPS = [
  {
    id: 'share',
    title: 'Share checklist',
    do: 'Click Start run first. As the case manager, open Feedback → Send New and go to step 2. Look at Share a view-only copy. Use a meeting type other than Reevaluation, then switch to Reevaluation. Uncheck one person, then change the meeting type again.',
    checks: [
      {
        id: 'listed',
        label:
          'Step 2 lists people, each with a type (case manager, psych, speech, OT, …). No dropdown.'
      },
      {
        id: 'defaults',
        label:
          'Before Reevaluation, Speech and Mental health start checked. Other provider types appear only if App Settings includes them. The assigned psych does not. Skip if this student has none of those.'
      },
      {
        id: 'districtSupport',
        label:
          'Assigned District support is listed and starts checked on a new form. Skip if this student has no District support.'
      },
      {
        id: 'reeval',
        label:
          'On Reevaluation, the assigned psych starts checked. Skip if this student has no psych.'
      },
      {
        id: 'sticky',
        label: 'After you uncheck someone, changing the meeting type leaves your choice in place.'
      }
    ]
  },
  {
    id: 'send',
    title: 'Send the form',
    do: 'Finish Send. Then open the form link in this same browser tab (a new tab will not see this log).',
    checks: [
      { id: 'created', label: 'The form was created' },
      {
        id: 'email',
        label:
          'The teacher received the email, or you copied the link after email could not be sent'
      }
    ]
  },
  {
    id: 'teacher',
    title: 'Teacher fills it',
    do: 'On the form, submit answers. If the school allows editing after submit, change an answer and let it save. Then come back to this tab’s Students page.',
    checks: [
      { id: 'submit', label: 'The teacher submitted' },
      {
        id: 'edit',
        label:
          'A submitted answer could still be saved before Close now. Skip if this school does not allow that.'
      }
    ]
  },
  {
    id: 'viewer',
    title: 'Service provider view',
    do: 'With the dev role switcher, become a service provider who was checked. Open this student and use Feedback.',
    checks: [
      { id: 'readonly', label: 'The overview is view-only: Shared by …, and no Send New' },
      { id: 'responses', label: 'View Responses opens the submitted answers' }
    ]
  },
  {
    id: 'edit',
    title: 'Edit who can view',
    do: 'Switch back to the person who sent the form. Edit it, change the share checklist, and Save.',
    checks: [{ id: 'saved', label: 'Save wrote the new view-only list' }]
  },
  {
    id: 'close',
    title: 'Close now',
    do: 'Manage Close → Close now. Open the teacher form again in this same tab.',
    checks: [
      { id: 'closed', label: 'Close now saved' },
      { id: 'locked', label: 'The teacher form no longer accepts answers' }
    ]
  },
  {
    id: 'psych',
    title: 'Psych sends one',
    do: 'Optional. As a school psych, Send New for a student whose case manager is someone else. Stop on step 2.',
    checks: [
      { id: 'cm', label: 'The case manager is listed and checked, and you are not on the list' }
    ]
  }
]

const emptyState = () => ({
  startedAt: null,
  stepIndex: 0,
  checks: {},
  actions: []
})

export const feedbackSmokeState = reactive(emptyState())

function persist() {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(feedbackSmokeState))
  } catch {
    /* private mode / quota — the in-memory run still works */
  }
}

function hydrate() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return
    const saved = JSON.parse(raw)
    feedbackSmokeState.startedAt = saved.startedAt || null
    feedbackSmokeState.stepIndex = Number(saved.stepIndex) || 0
    feedbackSmokeState.checks = saved.checks && typeof saved.checks === 'object' ? saved.checks : {}
    feedbackSmokeState.actions = Array.isArray(saved.actions) ? saved.actions : []
  } catch {
    /* ignore a corrupt blob */
  }
}

hydrate()

export function startFeedbackSmoke() {
  if (feedbackSmokeState.startedAt) return
  feedbackSmokeState.startedAt = new Date().toISOString()
  persist()
}

export function logFeedbackSmoke(event, detail = {}) {
  if (!feedbackSmokeState.startedAt) return
  const row = { t: new Date().toISOString(), event }
  for (const [key, value] of Object.entries(detail)) {
    if (value == null || value === '') continue
    row[key] = value
  }
  feedbackSmokeState.actions.push(row)
  if (feedbackSmokeState.actions.length > 200) {
    feedbackSmokeState.actions.splice(0, feedbackSmokeState.actions.length - 200)
  }
  applyAutoChecks()
  persist()
}

function num(value) {
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

function setAutoCheck(id, status, note) {
  const prev = feedbackSmokeState.checks[id]
  if (prev && prev.auto === false) return
  feedbackSmokeState.checks[id] = { status, note, auto: true }
}

export function feedbackAutoMarksFromActions(actions) {
  const rows = Array.isArray(actions) ? actions : []
  const marks = {}
  const set = (id, status, note) => {
    marks[id] = { status, note }
  }
  const has = (event, pred = () => true) => rows.some((row) => row.event === event && pred(row))
  const share = (pred) => has('share-state', pred)

  if (share((row) => num(row.options) > 0)) {
    set('share.listed', 'pass', 'From the log: step 2 listed people to share with.')
  }
  if (
    share(
      (row) =>
        String(row.touched) === '0' &&
        num(row.districtSupportCount) > 0 &&
        num(row.districtSupportOn) === num(row.districtSupportCount)
    )
  ) {
    set(
      'share.districtSupport',
      'pass',
      'From the log: assigned District support appeared checked before any sharing edits.'
    )
  }
  if (
    share(
      (row) =>
        String(row.touched) !== '1' &&
        row.meetingType !== 'Reevaluation' &&
        num(row.providerCount) > 0 &&
        num(row.providerOn) === num(row.providerCount) &&
        (num(row.psychCount) === 0 || num(row.psychOn) === 0)
    )
  ) {
    set(
      'share.defaults',
      'pass',
      'From the log: providers started checked, and the psych did not, before Reevaluation.'
    )
  }
  if (
    share(
      (row) =>
        String(row.touched) !== '1' &&
        row.meetingType === 'Reevaluation' &&
        num(row.psychCount) > 0 &&
        num(row.psychOn) === 1
    )
  ) {
    set('share.reeval', 'pass', 'From the log: on Reevaluation the assigned psych started checked.')
  }
  if (has('share-sticky')) {
    set(
      'share.sticky',
      'pass',
      'From the log: the meeting type changed after you edited the checklist, so defaults were not put back.'
    )
  }
  if (has('send-ok')) {
    set('send.created', 'pass', 'From the log: the form was created.')
  }
  if (has('submit-ok')) {
    set('teacher.submit', 'pass', 'From the log: the teacher submitted.')
  }
  if (has('edit-after-submit')) {
    set(
      'teacher.edit',
      'pass',
      'From the log: a submitted answer was saved before the form was closed.'
    )
  }
  if (has('overview', (row) => num(row.shared) > 0 && String(row.canSend) !== '1')) {
    set(
      'viewer.readonly',
      'pass',
      'From the log: a shared form was open and Send New was not available.'
    )
  }
  if (has('view-responses', (row) => String(row.shared) === '1')) {
    set(
      'viewer.responses',
      'pass',
      'From the log: View Responses opened a form shared with this person.'
    )
  }
  if (has('edit-save', (row) => String(row.viewers) === '1')) {
    set('edit.saved', 'pass', 'From the log: Edit saved a new view-only list.')
  }
  if (has('close-now')) {
    set('close.closed', 'pass', 'From the log: Close now saved.')
  }
  const closedAt = rows.findIndex((row) => row.event === 'close-now')
  if (
    closedAt >= 0 &&
    rows
      .slice(closedAt + 1)
      .some((row) => row.event === 'form-closed' && String(row.canEdit) !== '1')
  ) {
    set(
      'close.locked',
      'pass',
      'From the log: after Close now the teacher form would not take answers.'
    )
  }
  if (
    share(
      (row) =>
        row.role === 'school_psych' &&
        num(row.cmCount) > 0 &&
        num(row.cmOn) === 1 &&
        String(row.selfListed) !== '1'
    )
  ) {
    set(
      'psych.cm',
      'pass',
      'From the log: the psych’s step 2 listed the case manager, checked, and did not list the sender.'
    )
  }
  return marks
}

function applyAutoChecks() {
  const marks = feedbackAutoMarksFromActions(feedbackSmokeState.actions)
  for (const [id, mark] of Object.entries(marks)) {
    setAutoCheck(id, mark.status, mark.note)
  }
}

export function setFeedbackSmokeCheck(id, status, note) {
  startFeedbackSmoke()
  const prev = feedbackSmokeState.checks[id] || {}
  feedbackSmokeState.checks[id] = {
    status: status == null ? prev.status || '' : status,
    note: note != null ? note : prev.note || '',
    auto: false
  }
  persist()
}

export function setFeedbackSmokeStep(index) {
  const max = FEEDBACK_SMOKE_STEPS.length - 1
  feedbackSmokeState.stepIndex = Math.min(max, Math.max(0, index))
  persist()
}

export function resetFeedbackSmoke() {
  const fresh = emptyState()
  feedbackSmokeState.startedAt = fresh.startedAt
  feedbackSmokeState.stepIndex = 0
  feedbackSmokeState.checks = {}
  feedbackSmokeState.actions = []
  persist()
}

function allChecks() {
  const rows = []
  for (const step of FEEDBACK_SMOKE_STEPS) {
    for (const check of step.checks) {
      const saved = feedbackSmokeState.checks[`${step.id}.${check.id}`] || {}
      rows.push({
        step: step.title,
        label: check.label,
        status: saved.status || 'unmarked',
        note: saved.note || ''
      })
    }
  }
  return rows
}

export function feedbackSmokeReport() {
  const checks = allChecks()
  const counts = { pass: 0, fail: 0, skip: 0, unmarked: 0 }
  for (const row of checks) counts[row.status] = (counts[row.status] || 0) + 1
  return {
    kind: 'feedback-form-manual-smoke',
    startedAt: feedbackSmokeState.startedAt,
    copiedAt: new Date().toISOString(),
    counts,
    checks,
    actions: feedbackSmokeState.actions.slice()
  }
}

export function feedbackSmokeMarkdown() {
  const report = feedbackSmokeReport()
  const lines = [
    'Feedback form smoke',
    `started: ${report.startedAt || '—'}`,
    `pass ${report.counts.pass} · fail ${report.counts.fail} · skip ${report.counts.skip} · unmarked ${report.counts.unmarked}`,
    ''
  ]
  let lastStep = ''
  for (const row of report.checks) {
    if (row.step !== lastStep) {
      lines.push(row.step)
      lastStep = row.step
    }
    const mark = row.status === 'pass' ? 'pass' : row.status === 'fail' ? 'FAIL' : row.status
    lines.push(`- ${mark}: ${row.label}${row.note ? ` — ${row.note}` : ''}`)
  }
  lines.push('', 'Actions')
  if (!report.actions.length) lines.push('(none yet)')
  for (const action of report.actions) {
    const extra = Object.entries(action)
      .filter(([key]) => key !== 't' && key !== 'event')
      .map(([key, value]) => `${key}=${Array.isArray(value) ? value.join(', ') : value}`)
      .join(' ')
    lines.push(`${action.t.slice(11, 19)} ${action.event}${extra ? ` ${extra}` : ''}`)
  }
  return lines.join('\n')
}
